/**
 * RiskAssessmentModule - Main Entry Component
 *
 * The main component for the Risk Assessment module.
 * Uses SeaReady's standard 2/3 + 1/3 layout with full-screen form pattern.
 *
 * @module components/RiskAssessmentModule
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
// FT-357: shell-level back handler — register a "back to dashboard" handler
// so mobile users always see a Back button in the bottom bar.
import { useSetBackHandler } from '../../../contexts/NavigationBackContext';
import {
  AlertTriangle,
  Plus,
  FileText,
  Clock,
  Edit2,
  Trash2,
  Search,
  ArrowRight,
  Download,
  Building2  // FT-116: Icon for "Add from Organisation"
} from 'lucide-react';

import Dropdown from '../../../components/common/Dropdown';
import { RiskAssessmentForm } from './RiskAssessmentForm';
import { RiskAssessmentSidebar } from './RiskAssessmentSidebar';
import RiskAssessmentDetailView from './RiskAssessmentDetailView';
import RAHistoryViewer from './RAHistoryViewer';
import RAReviewSchedule from './RAReviewSchedule';
import RiskAssessmentSettingsView from './RiskAssessmentSettingsView';
import { RiskLevelBadge } from './RiskMatrix';
import AddRAFromOrgModal from './AddRAFromOrgModal';  // FT-116: Add from Organisation modal
import { useFeatureFlags } from '../hooks/useFeatureFlags';
// FT-116 REWRITE: All RAs now come from fleet storage - useRiskAssessments removed
import { useFleetRiskAssessments } from '../../../hooks/domain/fleet/useFleetRiskAssessments';
import { useAuditTrail } from '../../../hooks/useAuditTrail';
import { MODULE_INFO } from '../index';
import { STATUS_OPTIONS } from '../config/moduleConfig';
import { useVessel } from '../../../contexts/VesselContext';
import { getContext } from '../../../lib/context';
import {
  exportIndividualRiskAssessmentPDF,
  exportRiskAssessmentRegisterPDF,
  exportHighRiskReportPDF
} from '../../../utils/pdf/riskAssessmentExport';

/**
 * Calculate review status for a risk assessment
 * @param {Object} assessment - The assessment object
 * @returns {{ status: string, label: string, daysUntil: number, color: string } | null}
 */
const getReviewStatus = (assessment) => {
  if (!assessment.nextReviewDate || assessment.status !== 'active') {
    return null;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const reviewDate = new Date(assessment.nextReviewDate);
  reviewDate.setHours(0, 0, 0, 0);

  const daysUntil = Math.ceil((reviewDate - today) / (1000 * 60 * 60 * 24));

  if (daysUntil < 0) {
    return {
      status: 'overdue',
      label: `${Math.abs(daysUntil)} days overdue`,
      daysUntil,
      color: 'red'
    };
  } else if (daysUntil <= 30) {
    return {
      status: 'due_soon',
      label: daysUntil === 0 ? 'Review due today' : `Review in ${daysUntil} days`,
      daysUntil,
      color: 'amber'
    };
  } else if (daysUntil <= 90) {
    return {
      status: 'upcoming',
      label: `Review in ${daysUntil} days`,
      daysUntil,
      color: 'blue'
    };
  }

  return null; // Don't show badge if >90 days away
};

/**
 * RiskAssessmentModule Component
 * @param {Object} props - Component props
 * @param {Object} props.user - Current user object (deprecated - use VesselContext)
 * @param {string} props.vesselId - Current vessel ID (optional - uses context if not provided)
 */
export const RiskAssessmentModule = ({ user = null, vesselId: vesselIdProp = null, setActiveModule = null }) => {
  // FT-357: register a shell-level "back to dashboard" handler so mobile
  // users always see a Back button in the bottom bar for this module.
  // Nested drill-downs (assessment form, detail view, review schedule,
  // settings) push their own handlers on top and pop back on unmount.
  useSetBackHandler(
    useCallback(() => {
      if (setActiveModule) setActiveModule('dashboard');
    }, [setActiveModule])
  );

  // Get vessel data from context (standard SeaReady pattern)
  const { currentVessel, currentVesselId, getVesselSettings } = useVessel();

  // Use prop if provided, otherwise use context
  const vesselId = vesselIdProp || currentVesselId;
  const vesselSettings = getVesselSettings() || {};

  // Get company name from context
  const context = getContext(vesselId);
  const companyName = context.companyName || '';

  const flags = useFeatureFlags(user);

  // FT-116 REWRITE: Single data source - all RAs from fleet storage
  // FT-120: Removed allFleetRAs/getByVessel - vessel filter dropdown removed
  // FT-175: Added archive for soft delete, remove now uses tombstone pattern
  const {
    vesselRiskAssessments,      // RAs assigned to current vessel
    getUnassignedToCurrentVessel,
    update: updateAssessment,
    archive: archiveAssessment,   // FT-175: Soft delete (status=archived)
    remove: deleteAssessment      // FT-175: Permanent delete (tombstone)
  } = useFleetRiskAssessments();

  // Fleet hook uses localStorage (synchronous), no loading/error states needed
  const loading = false;
  const error = null;

  // Audit trail hook
  const {
    logChange,
    showDeleteDialog,
    DeleteModal
  } = useAuditTrail('risk-assessment');

  const [showAssessmentForm, setShowAssessmentForm] = useState(false);
  const [editingAssessment, setEditingAssessment] = useState(null);
  const [viewingAssessmentId, setViewingAssessmentId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [historyAssessmentId, setHistoryAssessmentId] = useState(null);
  const [showReviewSchedule, setShowReviewSchedule] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRisk, setFilterRisk] = useState('all');
  // FT-120: Removed filterVessel state - always show current vessel's RAs
  const [showAddFromOrgModal, setShowAddFromOrgModal] = useState(false);  // FT-116

  // Desktop breakpoint detection for table vs card view
  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' ? window.innerWidth >= 1024 : false);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // FT-120: Always show current vessel's RAs (removed vessel filter dropdown)
  // vesselRiskAssessments is already memoized in useFleetRiskAssessments
  const allAssessments = vesselRiskAssessments || [];

  // FT-116 REWRITE: Look up viewingAssessment from allAssessments
  const viewingAssessment = viewingAssessmentId
    ? allAssessments.find(a => a.id === viewingAssessmentId)
    : null;

  // FT-116 REWRITE: Calculate stats from fleet RAs (for current vessel)
  const stats = useMemo(() => {
    const ras = vesselRiskAssessments || [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return {
      total: ras.length,
      extreme: ras.filter(r => r.riskLevel === 'extreme').length,
      high: ras.filter(r => r.riskLevel === 'high').length,
      medium: ras.filter(r => r.riskLevel === 'medium').length,
      low: ras.filter(r => r.riskLevel === 'low').length,
      overdue: ras.filter(r => {
        if (!r.nextReviewDate || r.status !== 'active') return false;
        return new Date(r.nextReviewDate) < today;
      }).length,
      reviewDue: ras.filter(r => {
        if (!r.nextReviewDate || r.status !== 'active') return false;
        const reviewDate = new Date(r.nextReviewDate);
        const daysUntil = Math.ceil((reviewDate - today) / (1000 * 60 * 60 * 24));
        return daysUntil >= 0 && daysUntil <= 30;
      }).length
    };
  }, [vesselRiskAssessments]);

  // Filter assessments
  const filteredAssessments = useMemo(() => {
    return allAssessments.filter(assessment => {
      // Search filter
      if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        const matchesSearch =
          assessment.title?.toLowerCase().includes(lower) ||
          assessment.description?.toLowerCase().includes(lower) ||
          assessment.location?.toLowerCase().includes(lower);
        if (!matchesSearch) return false;
      }

      // Status filter - includes review due filters
      if (filterStatus === 'review_due') {
        const reviewStatus = getReviewStatus(assessment);
        if (!reviewStatus || reviewStatus.status === 'overdue') return false;
      } else if (filterStatus === 'overdue') {
        const reviewStatus = getReviewStatus(assessment);
        if (!reviewStatus || reviewStatus.status !== 'overdue') return false;
      } else if (filterStatus !== 'all' && assessment.status !== filterStatus) {
        return false;
      }

      // Risk level filter
      if (filterRisk !== 'all' && assessment.riskLevel !== filterRisk) {
        return false;
      }

      return true;
    });
  }, [allAssessments, searchTerm, filterStatus, filterRisk]);

  // Calculate overdue count for sidebar badge
  const overdueCount = useMemo(() => {
    return allAssessments.filter(a => {
      if (!a.nextReviewDate || a.status !== 'active') return false;
      return new Date() > new Date(a.nextReviewDate);
    }).length;
  }, [allAssessments]);

  const handleCreateNew = () => {
    setEditingAssessment(null);
    setShowAssessmentForm(true);
  };

  // FT-116: Handler for RA assigned from organisation
  const handleRAAssignedFromOrg = (ra) => {
    // Refresh happens automatically via hook reactivity
    // Optionally show a success message
    console.log('[RiskAssessmentModule] FT-116: RA assigned from org:', ra.title);
  };

  // FT-116: Count of unassigned org RAs available
  const unassignedOrgRACount = useMemo(() => {
    return getUnassignedToCurrentVessel().length;
  }, [getUnassignedToCurrentVessel]);

  const handleView = (assessment) => {
    setViewingAssessmentId(assessment.id);
  };

  const handleCloseDetail = () => {
    setViewingAssessmentId(null);
  };

  const handleEdit = (assessment) => {
    setViewingAssessmentId(null); // Close detail view if open
    setEditingAssessment(assessment);
    setShowAssessmentForm(true);
  };

  // FT-175: handleDelete now respects deleteType from dialog
  // soft = archive (status=archived, restorable via UI)
  // permanent = tombstone (deletedAt set, syncs to cloud, can be restored within 30 days)
  const handleDelete = async (assessment) => {
    // Show delete dialog with reason capture
    showDeleteDialog(assessment, async (reason) => {
      try {
        const deleteType = reason.deleteType || 'soft';

        // Log the deletion to audit trail
        await logChange({
          action: 'delete',
          item: {
            id: assessment.id,
            name: assessment.title,
            type: 'risk-assessment'
          },
          reason: reason.reason,
          authorizedBy: reason.authorizedBy,
          deleteType
        });

        // FT-175: Perform appropriate delete action based on deleteType
        if (deleteType === 'permanent') {
          // Tombstone with hardDelete flag for eventual cloud cleanup
          await deleteAssessment(assessment.id, true);
        } else {
          // Soft delete = archive (just sets status, visible in archived view)
          await archiveAssessment(assessment.id);
        }

        setViewingAssessmentId(null);
      } catch (e) {
        console.error('Failed to delete:', e);
        alert('Failed to delete assessment. Please try again.');
      }
    });
  };

  const handleExportIndividual = (assessment) => {
    exportIndividualRiskAssessmentPDF(assessment, {
      vesselName: currentVessel?.name || 'Unknown Vessel',
      category: currentVessel?.category || vesselSettings?.category || 'Unknown Category',
      vesselSettings: {
        officialNumber: vesselSettings?.officialNumber || currentVessel?.officialNumber || '',
        grossTonnage: vesselSettings?.grossTonnage || currentVessel?.grossTonnage || ''
      },
      companyName
    });
  };

  const handleFormBack = () => {
    setShowAssessmentForm(false);
    setEditingAssessment(null);
  };

  const handleFormSave = (savedAssessment) => {
    setShowAssessmentForm(false);
    setEditingAssessment(null);
    // Assessments list will auto-update from hook
  };

  const handleExportRegister = () => {
    exportRiskAssessmentRegisterPDF(filteredAssessments, {
      vesselName: currentVessel?.name || 'Unknown Vessel',
      category: currentVessel?.category || vesselSettings?.category || 'Unknown Category',
      vesselSettings: {
        officialNumber: vesselSettings?.officialNumber || currentVessel?.officialNumber || '',
        grossTonnage: vesselSettings?.grossTonnage || currentVessel?.grossTonnage || ''
      },
      companyName,
      filterStatus: filterStatus !== 'all' ? filterStatus : undefined,
      filterRiskLevel: filterRisk !== 'all' ? filterRisk : undefined
    });
  };

  const handleExportHighRisk = () => {
    const highRiskAssessments = allAssessments.filter(a => (a.residualRiskScore || 0) >= 12);

    if (highRiskAssessments.length === 0) {
      alert('No high or extreme risk assessments found.');
      return;
    }

    exportHighRiskReportPDF(highRiskAssessments, {
      vesselName: currentVessel?.name || 'Unknown Vessel',
      category: currentVessel?.category || vesselSettings?.category || 'Unknown Category',
      vesselSettings: {
        officialNumber: vesselSettings?.officialNumber || currentVessel?.officialNumber || '',
        grossTonnage: vesselSettings?.grossTonnage || currentVessel?.grossTonnage || ''
      },
      companyName
    });
  };

  const handleOpenSettings = () => {
    setShowSettings(true);
  };

  // Handler for updating signatures
  const handleUpdateSignature = async (signatures) => {
    if (!viewingAssessmentId) return;

    try {
      await updateAssessment(viewingAssessmentId, { signatures });

      // Log signature addition
      const signatureType = signatures.assessor && !viewingAssessment?.signatures?.assessor
        ? 'assessor'
        : signatures.approver && !viewingAssessment?.signatures?.approver
          ? 'approver'
          : 'updated';

      await logChange({
        action: 'update',
        item: {
          id: viewingAssessmentId,
          name: viewingAssessment?.title,
          type: 'risk-assessment'
        },
        changes: {
          field: 'signature',
          type: signatureType,
          signedBy: signatureType === 'assessor'
            ? signatures.assessor?.name
            : signatures.approver?.name
        }
      });
    } catch (err) {
      console.error('Failed to save signature:', err);
      alert('Failed to save signature. Please try again.');
    }
  };

  // Show history viewer
  if (showHistory) {
    return (
      <RAHistoryViewer
        onBack={() => {
          setShowHistory(false);
          setHistoryAssessmentId(null);
        }}
        assessmentId={historyAssessmentId}
        assessments={allAssessments}
      />
    );
  }

  // Show review schedule
  if (showReviewSchedule) {
    return (
      <RAReviewSchedule
        onBack={() => setShowReviewSchedule(false)}
        assessments={allAssessments}
        vesselName={currentVessel?.name || ''}
      />
    );
  }

  // Show full-screen settings view
  if (showSettings) {
    return (
      <RiskAssessmentSettingsView
        onBack={() => setShowSettings(false)}
      />
    );
  }

  // Show full-screen detail view when viewing (SeaReady pattern)
  if (viewingAssessment) {
    return (
      <RiskAssessmentDetailView
        assessment={viewingAssessment}
        vesselName={currentVessel?.name || 'Unknown Vessel'}
        vesselConfig={currentVessel || {}}
        vesselSettings={{
          officialNumber: vesselSettings?.officialNumber || currentVessel?.officialNumber || '',
          grossTonnage: vesselSettings?.grossTonnage || currentVessel?.grossTonnage || ''
        }}
        companyName={companyName}
        onBack={handleCloseDetail}
        onEdit={() => handleEdit(viewingAssessment)}
        onDelete={() => handleDelete(viewingAssessment)}
        onExport={() => handleExportIndividual(viewingAssessment)}
        onUpdateSignature={handleUpdateSignature}
        onViewHistory={() => {
          setHistoryAssessmentId(viewingAssessmentId);
          setShowHistory(true);
        }}
      />
    );
  }

  // Show full-screen form when creating/editing (SeaReady pattern)
  if (showAssessmentForm) {
    return (
      <RiskAssessmentForm
        assessment={editingAssessment}
        onSave={handleFormSave}
        onBack={handleFormBack}
        vesselId={vesselId}
      />
    );
  }

  return (
    <div>
      {/* Module Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <AlertTriangle className="h-7 w-7 mr-2 text-yellow-500" />
          Risk Assessments
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Identify hazards, assess risks, and implement control measures
        </p>
      </div>

      {/* 2/3 + 1/3 Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content - 2/3 width */}
        <div className="lg:col-span-2 space-y-6">
          {/* Assessments List - with integrated search in header */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Header with integrated search */}
            <div className="bg-slate-700 px-5 py-3 rounded-t-2xl">
              <div className="flex items-center justify-between gap-6">
                {/* Title */}
                <h3 className="text-base font-semibold text-white whitespace-nowrap flex-shrink-0">
                  Risk Assessments ({filteredAssessments.length})
                </h3>

                {/* Search Input - INSIDE header */}
                <div className="relative flex-1 max-w-2xl">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search assessments..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 bg-white text-slate-800 text-sm border border-white/20 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all duration-200"
                  />
                </div>

                {/* FT-116: Add from Organisation button */}
                {unassignedOrgRACount > 0 && (
                  <button
                    onClick={() => setShowAddFromOrgModal(true)}
                    className="px-3 py-1.5 bg-slate-600 text-white text-sm rounded-md hover:bg-slate-500 transition-colors flex items-center gap-1"
                    title={`${unassignedOrgRACount} organisation RAs available`}
                  >
                    <Building2 className="h-4 w-4" />
                    From Org
                    <span className="ml-1 px-1.5 py-0.5 bg-white/20 rounded text-xs">
                      {unassignedOrgRACount}
                    </span>
                  </button>
                )}

                {/* New button */}
                <button
                  onClick={handleCreateNew}
                  className="px-3 py-1.5 bg-cyan-600 text-white text-sm rounded-md hover:bg-cyan-700 transition-colors flex items-center gap-1"
                >
                  <Plus className="h-4 w-4" />
                  New
                </button>
              </div>
            </div>

            {/* Content Section */}
            <div className="px-6 py-5">
              {/* Filter Dropdowns - standalone row below header */}
              {/* FT-120: Removed vessel filter dropdown - use top-of-page vessel switcher instead */}
              <div className="flex flex-wrap gap-3 mb-6">
                <Dropdown
                  value={filterStatus}
                  onChange={setFilterStatus}
                  width="w-48"
                  options={[
                    { value: 'all', label: 'All Statuses' },
                    { value: 'review_due', label: '⚠️ Review Due' },
                    { value: 'overdue', label: '🔴 Overdue Reviews' },
                    // Lite tier: only show draft, active, archived (exclude under_review and approved)
                    ...Object.entries(STATUS_OPTIONS)
                      .filter(([key]) => !['underReview', 'approved'].includes(key))
                      .map(([key, status]) => ({
                        value: status.value,
                        label: status.label
                      }))
                  ]}
                />
                <Dropdown
                  value={filterRisk}
                  onChange={setFilterRisk}
                  width="w-44"
                  options={[
                    { value: 'all', label: 'All Risk Levels' },
                    { value: 'extreme', label: 'Extreme' },
                    { value: 'high', label: 'High' },
                    { value: 'medium', label: 'Medium' },
                    { value: 'low', label: 'Low' }
                  ]}
                />
              </div>

            {loading ? (
              <div className="p-12 text-center">
                <div className="w-8 h-8 border-4 border-cyan-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-gray-500">Loading assessments...</p>
              </div>
            ) : error ? (
              <div className="p-12 text-center">
                <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-3" />
                <p className="text-red-600">{error}</p>
              </div>
            ) : filteredAssessments.length === 0 ? (
              <div className="p-12 text-center">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600 font-medium">
                  {allAssessments.length === 0
                    ? 'No risk assessments yet'
                    : 'No assessments match your filters'
                  }
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {allAssessments.length === 0
                    ? 'Click "Create Assessment" to get started'
                    : 'Try adjusting your search or filters'
                  }
                </p>
                {allAssessments.length === 0 && (
                  <button
                    onClick={handleCreateNew}
                    className="mt-4 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors inline-flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Create First Assessment
                  </button>
                )}
              </div>
            ) : isDesktop ? (
              <AssessmentTable
                assessments={filteredAssessments}
                onView={(assessment) => handleView(assessment)}
                onEdit={(assessment) => handleEdit(assessment)}
                onDelete={(assessment) => handleDelete(assessment)}
                onExport={(assessment) => handleExportIndividual(assessment)}
              />
            ) : (
              <div className="divide-y divide-gray-200 bg-white rounded-2xl shadow-sm border border-gray-200">
                {filteredAssessments.map(assessment => (
                  <AssessmentRow
                    key={assessment.id}
                    assessment={assessment}
                    onView={() => handleView(assessment)}
                    onEdit={() => handleEdit(assessment)}
                    onDelete={() => handleDelete(assessment)}
                    onExport={() => handleExportIndividual(assessment)}
                  />
                ))}
              </div>
            )}
            </div>
          </div>

          {/* Compliance Badges */}
          <div className="flex flex-wrap gap-2">
            {MODULE_INFO.compliance.map(code => (
              <span
                key={code}
                className="px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full text-sm font-medium"
              >
                {code} Compliant
              </span>
            ))}
          </div>
        </div>

        {/* Sidebar - 1/3 width */}
        <RiskAssessmentSidebar
          stats={stats}
          onCreateAssessment={handleCreateNew}
          onExportRegister={handleExportRegister}
          onExportHighRisk={handleExportHighRisk}
          onOpenSettings={handleOpenSettings}
          onViewHistory={() => setShowHistory(true)}
          onViewReviewSchedule={() => setShowReviewSchedule(true)}
          overdueCount={overdueCount}
        />

        {/* Delete Modal with Reason Capture */}
        <DeleteModal />

        {/* FT-116: Add from Organisation Modal */}
        <AddRAFromOrgModal
          isOpen={showAddFromOrgModal}
          onClose={() => setShowAddFromOrgModal(false)}
          onAssign={handleRAAssignedFromOrg}
        />
      </div>
    </div>
  );
};

/**
 * Assessment Row Component
 */
const AssessmentRow = ({ assessment, onView, onEdit, onDelete, onExport }) => {
  const residualScore = assessment.residualRiskScore || 0;
  const initialScore = assessment.initialRiskScore || 0;
  const hazardCount = assessment.hazards?.length || 0;
  const statusInfo = STATUS_OPTIONS[assessment.status] || STATUS_OPTIONS.draft;
  const reviewStatus = getReviewStatus(assessment);

  return (
    <div
      onClick={onView}
      className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
    >
      <div className="flex items-start justify-between gap-4">
        {/* Main Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h4 className="font-medium text-gray-900 truncate">
              {assessment.title || 'Untitled Assessment'}
            </h4>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              statusInfo.color === 'green' ? 'bg-green-100 text-green-800' :
              statusInfo.color === 'blue' ? 'bg-blue-100 text-blue-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {statusInfo.label}
            </span>

            {/* Review Status Badge */}
            {reviewStatus && (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 ${
                reviewStatus.color === 'red' ? 'bg-red-100 text-red-800' :
                reviewStatus.color === 'amber' ? 'bg-amber-100 text-amber-800' :
                'bg-blue-100 text-blue-800'
              }`}>
                <Clock className="h-3 w-3" />
                {reviewStatus.status === 'overdue' ? 'Review Overdue' : 'Review Due'}
              </span>
            )}
          </div>

          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
            {assessment.location && (
              <span>{assessment.location}</span>
            )}
            <span>{hazardCount} hazard{hazardCount !== 1 ? 's' : ''}</span>
            {assessment.createdAt && (
              <span>
                Created {new Date(assessment.createdAt).toLocaleDateString()}
              </span>
            )}
            {/* Show review date for active assessments */}
            {reviewStatus && (
              <span className={reviewStatus.color === 'red' ? 'text-red-600 font-medium' : ''}>
                {reviewStatus.label}
              </span>
            )}
          </div>

          {/* Risk Scores */}
          <div className="flex items-center gap-3 mt-3">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Initial:</span>
              <RiskLevelBadge score={initialScore} showLabel={false} />
            </div>
            <ArrowRight className="h-4 w-4 text-gray-400" />
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Residual:</span>
              <RiskLevelBadge score={residualScore} />
            </div>
          </div>
        </div>

        {/* Actions - stop propagation to prevent card click */}
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onExport();
            }}
            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Download PDF"
          >
            <Download className="h-5 w-5" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            className="p-2 text-gray-500 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors"
            title="Edit"
          >
            <Edit2 className="h-5 w-5" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Assessment Table Component - Desktop View
 */
const AssessmentTable = ({ assessments, onView, onEdit, onDelete, onExport }) => {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-700 text-white">
              <th className="px-4 py-3 text-left text-sm font-medium">Assessment</th>
              <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
              <th className="px-4 py-3 text-center text-sm font-medium">Hazards</th>
              <th className="px-4 py-3 text-center text-sm font-medium">Risk Level</th>
              <th className="px-4 py-3 text-left text-sm font-medium">Review Due</th>
              <th className="px-4 py-3 text-center text-sm font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {assessments.map((assessment) => {
              const statusInfo = STATUS_OPTIONS[assessment.status] || STATUS_OPTIONS.draft;
              const reviewStatus = getReviewStatus(assessment);
              const hazardCount = assessment.hazards?.length || 0;
              const residualScore = assessment.residualRiskScore || 0;

              return (
                <tr
                  key={assessment.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => onView(assessment)}
                >
                  {/* Assessment Name + Category */}
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">
                      {assessment.title || 'Untitled Assessment'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {assessment.category || 'No category'}
                    </div>
                  </td>

                  {/* Status Badge */}
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      statusInfo.color === 'green' ? 'bg-green-100 text-green-800' :
                      statusInfo.color === 'blue' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {statusInfo.label}
                    </span>
                  </td>

                  {/* Hazard Count */}
                  <td className="px-4 py-3 text-center text-sm text-gray-600">
                    {hazardCount}
                  </td>

                  {/* Risk Level - Residual Score */}
                  <td className="px-4 py-3 text-center">
                    <RiskLevelBadge score={residualScore} showLabel={false} />
                  </td>

                  {/* Review Due */}
                  <td className="px-4 py-3">
                    {reviewStatus ? (
                      <span className={`text-sm ${
                        reviewStatus.color === 'red' ? 'text-red-600 font-medium' :
                        reviewStatus.color === 'amber' ? 'text-amber-600' :
                        'text-gray-600'
                      }`}>
                        {reviewStatus.label}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onExport(assessment);
                        }}
                        className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Download PDF"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(assessment);
                        }}
                        className="p-1.5 text-gray-500 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(assessment);
                        }}
                        className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RiskAssessmentModule;
